import React, { useState, useEffect, forwardRef } from 'react';
import {
  Checkbox,
  formatSelectOptions,
  Input,
  Select,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { getBatchEditValue } from '../helpers';
import { useMessage } from '../hooks';
import { glossaryService } from '../services';

const _AddEntityTypeForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    entity_type_name: '',
    entity_type_code: '',
    entity_type_description: textareaInitialState({}),
    entity_type_is_default: false,
    glossary_id: null,
  };

  const initialBatchEditItems = {
    batch_glossary_id: false,
    batch_entity_type_is_default: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [glossaries, setGlossaries] = useState([]);
  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    glossaryService
      .getAll()
      .then(res => setGlossaries(res.glossaries))
      .catch(err => showMessage(err, 'error'));
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1 && mode !== 'add') {
      setFormData({
        entity_type_id: data[0].entity_type_id,
        entity_type_name: data[0].entity_type_name || '',
        entity_type_code: data[0].entity_type_code || '',
        entity_type_description: textareaInitialState({
          value: data[0].entity_type_description,
        }),
        entity_type_is_default: data[0].entity_type_is_default || false,
        glossary_id: data[0].glossary_id,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);
      setFormData({
        entity_type_is_default: getBatchEditValue(data, 'entity_type_is_default'),
        glossary_id: getBatchEditValue(data, 'glossary_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...initialFormState,
        glossary_id: data[0]?.glossary_id || null,
      });
    } else {
      resetForm();
    }
  }, [mode, data]);

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData({
      ...initialFormState,
      glossary_id: data[0]?.glossary_id || null,
    });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();
    setLoading(true);

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => ({
        entity_type_id: item.entity_type_id,
        entity_type_name: item.entity_type_name,
        entity_type_code: item.entity_type_code,
        entity_type_description: item.entity_type_description,
        entity_type_is_default: item.entity_type_is_default,
        glossary_id: item.glossary_id,
        ...tmp,
      }));
    } else {
      dataToSubmit = {
        ...formData,
        entity_type_description: textareaToRaw({
          value: formData.entity_type_description,
        }),
      };
    }

    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="entity_type_name"
              value={formData.entity_type_name || ''}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.entity_type_name
                  ? { valid: false, message: formErrors.entity_type_name }
                  : undefined
              }
            />
          </div>
        )}
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="entity_type_code"
              value={formData.entity_type_code || ''}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.entity_type_code
                  ? { valid: false, message: formErrors.entity_type_code }
                  : undefined
              }
              isOptional={true}
            />
          </div>
        )}

        {!batchEdit && (
          <div className="form-block vertical">
            <Textarea
              name="entity_type_description"
              value={formData.entity_type_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_glossary_id}
                name="batch_glossary_id"
                label="Edit Glossary"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="glossary_id"
            label={!batchEdit}
            value={formData.glossary_id === 'multiple' ? undefined : formData.glossary_id}
            options={formatSelectOptions({
              options: glossaries,
              optionValue: 'glossary_id',
              optionLabel: 'glossary_name',
            })}
            onChange={handleInputChange}
            placeholder={formData.glossary_id === 'multiple' ? '< Multiple >' : ''}
            disabled={loading || (batchEdit && !batchEditItems.batch_glossary_id)}
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_entity_type_is_default}
                name="batch_entity_type_is_default"
                label="Edit Is Default"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Checkbox
            type="checkbox"
            name="entity_type_is_default"
            checked={!!formData.entity_type_is_default}
            onChange={handleInputChange}
            disabled={loading || (batchEdit && !batchEditItems.batch_entity_type_is_default)}
          />
        </div>
      </form>
    </div>
  );
};

export const AddEntityTypeForm = forwardRef(_AddEntityTypeForm);
